import React, { useCallback, useEffect, useRef, useState } from 'react';
import FilterService from 'src/services/filter.service';
import { MenuSearchProps } from 'dls-react-filters';
import LiteralsService from 'src/services/literals/LiteralsService';

interface useFilteredOptionsReturn {
    filteredOptions: any[];
    handleFilter: (filter?: string, usePreviousData?: boolean) => void;
    searchProps: SearchProps;
    filterValue: string;
}

type SearchProps = Omit<MenuSearchProps, 'onClose' | 'focusOnSearchInput'>;

interface Props {
    options: any[];
    labelKey?: string;
    isChild?: (e: any) => boolean;
    anchorOnClickKey?: string;
    onSearchChange?: () => void;
}

function useFilteredOptions({
    options,
    labelKey = 'name',
    isChild = (e: any) => !!e.parentId,
    anchorOnClickKey = 'onClick',
    onSearchChange
}: Props): useFilteredOptionsReturn {
    const searchRef = useRef();
    const [_filteredOptions, _setFilteredOptions] = useState(options);
    const [filterValue, setFilterValue] = useState('');
    useEffect(() => {
        _setFilteredOptions(options);
    }, [options]);

    /**
     * Filter method to update state. Uses previous state when suitable
     * For example if a user types "e" followed by "d", then the `filteredOptions` calculated for "ed" are a subset
     * of those calculated for "e" and so we want to reuse the `filteredOptions` calculated for "e".
     */
    const handleFilter = useCallback(
        (filter = '', usePreviousData = false): void => {
            _setFilteredOptions(
                FilterService.filterOptions(
                    usePreviousData ? _filteredOptions : options,
                    filter.toLowerCase(),
                    labelKey,
                    isChild,
                    anchorOnClickKey
                )
            );
            setFilterValue(filter);
        },
        [_filteredOptions, options]
    );

    const searchProps: SearchProps = {
        id: 'filter-search',
        onChange: (e: React.ChangeEvent<any>) => {
            onSearchChange?.();
            const newFilter = e.target.value;
            handleFilter(newFilter, newFilter.startsWith(filterValue));
        },
        searchInputLabel: LiteralsService.getLiteral('General.search.ariaLabel'),
        value: filterValue,
        ref: searchRef,
        placeholder: LiteralsService.getLiteral('General.search.placeholder')
    };

    return { searchProps, filteredOptions: _filteredOptions, handleFilter, filterValue };
}

export default useFilteredOptions;
